import {Ref, ref, ToRef} from "vue";
import RequisiteRepository from "@/repositories/requisite/RequisiteRepository";
import Requisite from "@models/requisite/Requisite";
import {RequisitePsystemTargetType} from "@enums/RequisitePsystemTargetType";
import {PageOpenIn} from "@enums/PageOpenIn";
import RequisitePsystem from "@models/requisite/RequisitePsystem";
import {ServiceAddressTypes} from "@/services/v2/data/service-address-types/ServiceAddressTypes";

type DefaultPSystems = Record<RequisitePsystemTargetType, RequisitePsystem | null>

export default class RequisitePageController {
    private static _instance: RequisitePageController | null = null;

    public DEFAULT_PSYSTEM_TARGET: RequisitePsystemTargetType = RequisitePsystemTargetType.P2P;

    private _requisite: Ref<Requisite | null> = ref(null);
    private _openIn: PageOpenIn = PageOpenIn.PAGE;
    private readonly _defaultPSystems: DefaultPSystems = { p2p: null, wallet: null };
    private _selectedPSystemTargetType = ref<RequisitePsystemTargetType>(this.DEFAULT_PSYSTEM_TARGET);
    private _serviceAddressTypes: ServiceAddressTypes;

    private _requisiteRepository: RequisiteRepository;

    constructor(requisiteRepository: RequisiteRepository) {
        this._requisiteRepository = requisiteRepository;
        this._serviceAddressTypes = ServiceAddressTypes.of();
    }

    public static of(): RequisitePageController {
        if (RequisitePageController._instance === null) {
            RequisitePageController._instance = new RequisitePageController(
                new RequisiteRepository()
            );
        }
        return RequisitePageController._instance;
    }

    get selectedPSystemTargetType(): Ref<RequisitePsystemTargetType> {
        return this._selectedPSystemTargetType;
    }

    set selectedPSystemTargetType(value: RequisitePsystemTargetType) {
        this._selectedPSystemTargetType.value = value;
    }

    get openIn(): PageOpenIn {
        return this._openIn;
    }

    set openIn(value: PageOpenIn) {
        this._openIn = value;
    }

    get requisite(): ToRef<Requisite | null> {
        return this._requisite;
    }

    get defaultPSystems(): DefaultPSystems {
        return this._defaultPSystems;
    }

    set requisite(value: Requisite | null) {
        this._requisite.value = value;
    }

    get addressTypes() {
        return this._serviceAddressTypes.addressTypes;
    }

    async init(requisite: Requisite | null, pageOpenIn: PageOpenIn) {
        this._openIn = pageOpenIn;
        this._requisite.value = requisite;
        this._selectedPSystemTargetType.value = requisite?.psystem?.targetType ?? this.DEFAULT_PSYSTEM_TARGET;
        await this.fetchAndSetDefaultPsystems();
        await this.fetchAddressTypes();
    }

    private async fetchAndSetPsystems(psystemTargetType: RequisitePsystemTargetType) {
        const data = await this._requisiteRepository.getAllPsystems(psystemTargetType);
        this._defaultPSystems[psystemTargetType] = data[0];
    }

    private async fetchAndSetDefaultPsystems() {
        const data = await this._requisiteRepository.getAllPsystemsDefault();
        this._defaultPSystems.p2p = data.find((ps) => ps.targetType === RequisitePsystemTargetType.P2P) ?? null;
        this._defaultPSystems.wallet = data.find((ps) => ps.targetType === RequisitePsystemTargetType.WALLET) ?? null;
    }

    private async fetchAddressTypes() {
        await this._serviceAddressTypes.fetchAddressTypes();
    }

    public getPsystemByTargetType(psystemTargetType: RequisitePsystemTargetType) {
        return this._defaultPSystems[psystemTargetType];
    }

    async save() {
        if (this._requisite.value) {
            await this._requisiteRepository.patch(this._requisite.value);
        }
    }

    async create() {
        if (this._requisite.value) {
            await this._requisiteRepository.create(this._requisite.value);
        }
    }

    async delete(requisite: Requisite) {
        await this._requisiteRepository.delete(requisite);
    }
}
